import type { StoryObj } from '@storybook/html'
import type { CobaltTextarea as CobaltTextareaProps } from './cobalt-textarea'

export default {
  title: 'Components/Form/cobalt-textarea',
  argTypes: {
    label: {
      type: { name: 'string', required: true },
      description: 'This is the text which accompany the textarea. It is **required** for accessibility concerns.'
    },
    placeholder: {
      type: { name: 'string', required: true },
      description: 'Placeholder inside the input.'
    },
    format: {
      type: { name: 'string', required: true },
      options: ['outside', 'aside', 'hidden'],
      control: { type: 'select' },
      description: 'Position the label in different place.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'outside' }
      }
    },
    disabled: {
      control: 'boolean',
      options: [true, false],
      description: 'A boolean indicating the disable state of the Select field. The `aria-disabled` attribute relies on this property.',
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: false }
      }
    },
    requirement: {
      type: { name: 'string', required: true },
      options: ['mandatory', 'optional', 'none'],
      control: { type: 'select' },
      description: 'Mention displayed to indicate if the field is mandatory or optional. If none is selected `custom-requirement` slot is available.',
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: 'mandatory' }
      }
    },
    cobaltTextareaValueChange: {
      description: 'Custom event emitted when Textarea value change',
      table: {
        type: {
          summary: 'Integration',
          detail:
            'React: onCobaltTextareaValueChange={handleChange}\nVue:@cobaltTextareaValueChange="handleChange"\nAngular:(cobaltTextareaValueChange)="handleChange()"\nVanillaJs:.addEventListener("cobaltTextareaValueChange", (event) => {//handle change});'
        }
      },

      action: 'cobaltTextareaValueChange'
    }
  }
}

const Template = (args: CobaltTextareaProps) => {
  return `
  <cobalt-textarea label="${args.label}" placeholder="${args.placeholder}" requirement="${args.requirement}" format="${args.format}" disabled="${args.disabled}"></cobalt-textarea>
</div>
`
}

export const Default: StoryObj = Template.bind({})

Default.args = {
  label: 'Textarea Label',
  placeholder: 'Placeholder',
  format: 'outside',
  requirement: 'mandatory',
  disabled: false
}

const TemplateDescription = (args: CobaltTextareaProps) => {
  return `
<cobalt-textarea label="${args.label}" placeholder="${args.placeholder}" format="outside" description="${args.description}"></cobalt-textarea>
`
}

export const WithDescription: StoryObj = TemplateDescription.bind({})

WithDescription.args = {
  label: 'Textarea Label',
  placeholder: 'Placeholder',
  description: 'A little description which helps understand what the input can do.'
}

const TemplateCustomRequirement = (args: CobaltTextareaProps) => {
  return `
<style>
  .custom-requirement {
    padding-left: var(--number-spacing-4xsmall);
  }
</style>
<cobalt-textarea label="${args.label}" placeholder="${args.placeholder}" requirement="${args.requirement}">
  <span slot="custom-requirement" class="custom-requirement">- Requis</span>
</cobalt-textarea>
`
}

export const WithCustomRequirement: StoryObj = TemplateCustomRequirement.bind({})

WithCustomRequirement.args = {
  label: 'Textarea Label',
  placeholder: 'Placeholder',
  requirement: 'none'
}

const TemplateWithErrorMessage = (args: CobaltTextareaProps) => {
  return `
<script>
  document.querySelector('#toggle-button').addEventListener('cobaltButtonClick', (e) => {
    if (document.querySelector('#textarea-in-error').hasAttribute('error-message')) {
      document.querySelector('#textarea-in-error').removeAttribute('error-message')
    } else {
      document.querySelector('#textarea-in-error').setAttribute('error-message', 'This field is required.')
    }
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <cobalt-button id="toggle-button" label="Toggle Error State"></cobalt-button>
  <cobalt-textarea id="textarea-in-error" label="${args.label}" placeholder="${args.placeholder}" ></cobalt-textarea>
</div>
`
}

export const WithErrorMessage: StoryObj = TemplateWithErrorMessage.bind({})

WithErrorMessage.args = {
  label: 'Textarea Label',
  placeholder: 'Placeholder'
}

const TemplateOnChangeEvent = (args: CobaltTextareaProps) => {
  return `
  <script>
  document.querySelector('#textarea-on-change').addEventListener('cobaltTextareaValueChange', (e) => {
    document.querySelector('#textarea-value').textContent = e.detail
  })
</script>
<div style="display:flex; flex-direction: column; gap: var(--number-spacing-large);">
  <p class="typography-body-medium-regular">Value is: <span id="textarea-value">not defined yet</span></p>
  <cobalt-textarea id="textarea-on-change" label="${args.label}" placeholder="${args.placeholder}"></cobalt-textarea>
</div>
`
}

export const OnChangeEvent: StoryObj = TemplateOnChangeEvent.bind({})

OnChangeEvent.args = {
  label: 'Textarea Label',
  placeholder: 'Placeholder'
}

const selectOptions = [
  { value: 'Value1' },
  { value: 'Value2' },
  { value: 'Value3' },
  { value: 'Value4', isDisabled: true },
  { value: 'Value5' },
  { value: 'Value6' },
  { value: 'Value7' },
  { value: 'Value8' },
  { value: 'Value9' },
  { value: 'Value10' }
]

const TemplatePrefixSuffix = (args: CobaltTextareaProps) => {
  return `
  <cobalt-textarea label="${args.label}" placeholder="${args.placeholder}">
    <cobalt-icon-button slot="prefix" icon-name="star-solid" label="action"></cobalt-icon-button>
    <cobalt-select slot="suffix" options='${JSON.stringify(selectOptions)}' requirement="none" label="suffix select" format="hidden"></cobalt-select>
  </cobalt-textarea>
`
}

export const WithPrefixAndSuffix: StoryObj = TemplatePrefixSuffix.bind({})

WithPrefixAndSuffix.args = {
  label: 'Textarea Label',
  placeholder: 'Placeholder'
}
